import { useSuspenseQuery } from '@tanstack/react-query';
import { maxNetworksQueryOptions } from '#api/networks.queries.ts';
import { FlashIcon, LighthouseIcon } from '#assets/ReactSvg.ts';
import { Banner } from '#components/atoms/banner/Banner.tsx';
import { Link } from '#components/atoms/link/Link.tsx';
import { useUncoveredLighthouseNetworks, useUncoveredRelayNetworks } from '#hooks/useNetworkCoverage.ts';

type Props = {
  kind: 'lighthouse' | 'relay';
  className?: string;
};

/**
 * {@link NetworkCoverageBanner} is the "N networks have no lighthouse/relay coverage" banner shown
 * in the lighthouses/relays list-page subtitle, linking to network settings. It fetches its own
 * coverage data and renders nothing when no network has a gap — so mounting it only for the
 * relevant kind is what keeps host-kind pages from fanning out the per-network count queries.
 */
export function NetworkCoverageBanner({ kind, className }: Props) {
  // Split by kind so only the relevant coverage fan-out runs (each branch calls a single hook).
  return kind === 'lighthouse' ?
      <LighthouseCoverageBanner className={className} />
    : <RelayCoverageBanner className={className} />;
}

function LighthouseCoverageBanner({ className }: { className?: string }) {
  const networks = useSuspenseQuery(maxNetworksQueryOptions).data.data;
  const count = useUncoveredLighthouseNetworks(networks).length;
  return <CoverageBanner count={count} coverageNoun="lighthouse" className={className} />;
}

function RelayCoverageBanner({ className }: { className?: string }) {
  const networks = useSuspenseQuery(maxNetworksQueryOptions).data.data;
  const count = useUncoveredRelayNetworks(networks).length;
  return <CoverageBanner count={count} coverageNoun="relay" className={className} />;
}

/**
 * The presentational banner. Returns null at zero gaps so the subtitle slot stays empty (and
 * collapses) when there's nothing to warn about; otherwise it owns its own wrapper so the page can
 * treat "banner rendered" as a DOM fact.
 */
function CoverageBanner({
  count,
  coverageNoun,
  className,
}: {
  count: number;
  coverageNoun: 'lighthouse' | 'relay';
  className?: string;
}) {
  if (count === 0) return null;
  return (
    <div className={className}>
      <Banner variant="info" Icon={coverageNoun === 'relay' ? FlashIcon : LighthouseIcon}>
        {count} network{count === 1 ? '' : 's'} {count === 1 ? 'has' : 'have'} no {coverageNoun} coverage.{' '}
        <Link className="a" to="/settings/networks">
          Edit network{count === 1 ? '' : 's'}
        </Link>
      </Banner>
    </div>
  );
}
